import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { useMutation, useQuery } from 'react-query'
import { find, cloneDeep, isArray, isEmpty } from 'lodash-es'
import io from 'socket.io-client'
import { useCookies } from 'react-cookie'
import { useTranslation } from 'next-i18next'

// Libs
import { fetchRoomLivingData } from '@/api/fetchRoomLivingData'
import { fetchRoomData } from '@/api/fetchRoomData'
import { fetchRoomProductData } from '@/api/fetchRoomProductData'
import { fetchPlaybackInfo } from '@/api/fetchPlaybackInfo'
import { useAppSelector, useAppDispatch } from '@/redux/hooks'
import { setRoomGData, setRoomData, resetRoomData, setHasPlayback, setPlaybackData } from '@/redux/reducers/room'
import {
  resetSocket,
  setSocketUrl,
  setMessageList,
  resetSocketData,
  setSocketStatus,
  setReady,
  setIsNetworkError,
  setIsRoomIdChanged,
  setUserOffByHostOut,
  removeMicOnItem,
} from '@/redux/reducers/socket'
import { setMemberRole } from '@/redux/reducers/member'
import { setFixedPlayerData, resetFixedPlayerData } from '@/redux/reducers/common'
import { setUserIn } from '@/api/setUserIn'
import { LIVE_TRACK_EVENT, liveEnterSource, trackLiveEvent } from '@/methods/liveTracking'
import useLiveWatchHeartbeat from '@/hooks/useLiveWatchHeartbeat'
import { getRoomSocketInfo } from '@/methods/message/getRoomSocketInfo'
import { getRoomHandUpList } from '@/methods/message/getRoomHandUpList'
import { getRoomMicOnList } from '@/methods/message/getRoomMicOnList'
import { getRoomSocketUsers, getLimitMessageUsers } from '@/methods/message'
import { sendHeartBeat } from '@/methods/message/sendHeartBeat'
import { sendUserCount } from '@/methods/message/sendUserCount'
import { sendChathistory } from '@/methods/message/sendChathistory'
import { isMacOrIOS } from '@/utils/navigator'
import vbchange from '@/utils/vbchange'
import { handleSentryEvent } from '@/methods/handleSentryEvent'
import { handleMessageReceive } from '@/methods/message/handleMessageReceive'
import { sendReportStatus, useReportStatusContextGetter } from '@/methods/message/reportSessionStatus'

// Constants
import { QUERY_KEY } from '@/constants/queryKey'
import { ROOM_LIVE_TYPE } from '@/constants/liveRoom'
import { SOCKET_STATUS, STREAM_MODE } from '@/constants/socketEvent'
import { ROLE } from '@/constants/role'
import { COOKIE_DATA } from '@/constants/cookieKey'
import { STORAGE_DATA } from '@/constants/storageData'

// Types
import { ErrorData } from '@/types/index'
import { IRoomProductItem } from '@/types/room'

type RoomLivingData = ErrorData & {
  isRoomInfoDataFetched: boolean
  roomLivingData: any
  productList: IRoomProductItem[]
  isRoomLivingDataSettled: boolean
  setIsRoomLivingDataSettled: any
  isRoomInfoDataSettled: boolean
  setIsRoomInfoDataSettled: any
}

interface QueryParams {
  roomId: string
  memberInfoId: string // TODO: for testing
}

const defaultRoomLivingData = {
  gdata: {},
  liveUrlInfo: [],
  token: '',
}

const connectionOptions = {
  transports: ['websocket'],
  rejectUnauthorized: false,
  reconnection: false,
  reconnectionDelay: 5 * 1000,
  reconnectionDelayMax: 5 * 1000,
  reconnectionAttempts: 3,
  path: '/v2/socket.io',
  secure: false,
  autoConnect: true,
  timeout: 5 * 1000,
}

export const useLiveRoomData = ({
  isLessThenOneHour,
  isExternalProductData,
}: {
  isLessThenOneHour: boolean
  isExternalProductData?: boolean
}): RoomLivingData => {
  const socketData = useAppSelector((state) => state.socket)
  const memberData = useAppSelector((state) => state.member?.memberData)
  const gdata = useAppSelector((state) => state.room?.gdata)
  const roomData = useAppSelector((state) => state.room?.roomData)

  const { roomId, liveType } = roomData

  const setUserInMutation = useMutation(setUserIn)
  const { t } = useTranslation()

  const { socket, isReady, socketStatus, canHandUp, socketUrl, messageList } = socketData
  const socketRef = useRef<any>(socket)
  const socketStatusRef = useRef<any>(socketStatus)
  const socketUrlRef = useRef<any>(socketUrl)
  const socketDataRef = useRef<any>(socketData)
  const memberDataRef = useRef<any>(memberData)
  const trackedEnterRoomRef = useRef('') // 進房事件每場只記一次（socket 重連會再進這段）
  const [watchingRoomId, setWatchingRoomId] = useState('') // 觀眾進房後開始送觀看心跳
  const roomDataRef = useRef<any>(roomData)

  const [reconnectAfterVisible, setReconnectAfterVisible] = useState(false)

  const dispatch = useAppDispatch()

  const getReportStatusCtx = useReportStatusContextGetter()

  // websocket 生命週期事件統一拋 APM (tip.gif)：value = 事件、detail = 原因/補充
  const reportSocketApm = (value: string, detail = '') => {
    sendReportStatus({
      ctx: getReportStatusCtx(),
      token: socketDataRef.current?.userToken || '',
      id: socketUrlRef.current || '',
      key: 'WEBSOCKET',
      value,
      detail,
    })
  }

  const router = useRouter()
  const { query } = router
  const { roomId: sessionSn, memberInfoId } = query as unknown as QueryParams
  useLiveWatchHeartbeat({ roomId: watchingRoomId, enabled: !!watchingRoomId && watchingRoomId === sessionSn })
  const brandName = query.brandName as string
  const [cookies] = useCookies([COOKIE_DATA.PCHOME_UUID])
  const cookieUuid = !isEmpty(cookies[COOKIE_DATA.PCHOME_UUID]) ? cookies[COOKIE_DATA.PCHOME_UUID] : null
  const localStorageToken = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_DATA.TOKEN) : null

  const [isRoomLivingDataSettled, setIsRoomLivingDataSettled] = useState(false)
  const [isRoomInfoDataSettled, setIsRoomInfoDataSettled] = useState(false)

  useEffect(() => {
    if (liveType === ROOM_LIVE_TYPE.PAST) dispatch(resetFixedPlayerData())
  }, [liveType])

  useEffect(() => {
    socketDataRef.current = socketData
  }, [socketData])

  useEffect(() => {
    memberDataRef.current = memberData
  }, [memberData])

  useEffect(() => {
    roomDataRef.current = roomData
  }, [roomData])

  useEffect(() => {
    // 照理來講要離開直播間時清空，但沒辦法在 willUnMount 做 dispatch
    // 只好一進來先清空
    dispatch(resetSocketData())
  }, [])

  useEffect(() => {
    // 為了測試用，所以先留著
    const testTimer = setInterval(() => {
      console.log('current time', new Date())
    }, 30 * 1000)

    return () => {
      clearInterval(testTimer)
    }
  }, [])

  // 房間基本資訊
  const {
    data: roomInfoData,
    isFetched: isRoomInfoDataFetched,
    refetch: refetchRoomData,
  } = useQuery([QUERY_KEY.FETCH_ROOM_DATA, { sessionSn }], () => fetchRoomData({ roomId: sessionSn }), {
    enabled: !!sessionSn && router.isReady,
    cacheTime: 0,
    onSuccess: (data) => {
      dispatch(setRoomData({ roomData: data }))

      const { roomId, memberInfoId, coverPhotoUrl, roomPlacard, liveSourceType, liveSource } = data

      const fixedPlayerData = { roomId, coverPhotoUrl, hostMemberInfoId: memberInfoId, liveUrlInfo: [], liveSourceType, liveSource }

      dispatch(setFixedPlayerData({ fixedPlayerData }))

      const isPlacardExisted = find(messageList, { type: 'placard' })

      if (!isPlacardExisted && roomPlacard) {
        dispatch(
          setMessageList({
            messageData: {
              name: '',
              context: roomPlacard,
              type: 'placard',
              sort: 1,
            },
          }),
        )
      }
    },
    onSettled: () => {
      setIsRoomInfoDataSettled(true)
    },
  })

  // 房間產品資訊
  const { data: productList = [], isFetched: isProductListFetched } = useQuery(
    [QUERY_KEY.FETCH_ROOM_PRODUCT_DATA, { sessionSn }],
    () => fetchRoomProductData({ roomId: sessionSn }),
    {
      enabled: !!sessionSn && router.isReady && !isExternalProductData,
    },
  )

  // 回放資訊
  useQuery([QUERY_KEY.FETCH_PLAYBACK_INFO, { sessionSn }], () => fetchPlaybackInfo({ roomId: sessionSn }), {
    enabled: router.isReady && roomId === sessionSn && liveType === ROOM_LIVE_TYPE.PAST,
    onSuccess: (data) => {
      dispatch(setPlaybackData({ playbackData: data }))
      dispatch(setHasPlayback({ hasPlayback: true }))
    },
  })

  // 房間 socket 或串流資訊
  const {
    data = {},
    isFetched: isRoomLivingDataFetched,
    status,
    refetch: refetchRoomLivingData,
  } = useQuery(
    [QUERY_KEY.FETCH_ROOM_LIVING_DATA, { sessionSn }],
    () => fetchRoomLivingData({ sessionSn, memberInfoId, mode: isMacOrIOS ? STREAM_MODE.HLS : STREAM_MODE.FLV }),
    {
      enabled:
        router.isReady &&
        !!sessionSn &&
        isLessThenOneHour &&
        isRoomInfoDataFetched &&
        (roomInfoData?.liveType === ROOM_LIVE_TYPE.CURRENT || roomInfoData?.liveType === ROOM_LIVE_TYPE.FUTURE),
      onSuccess: (data) => {
        // test for changing gateways
        // data.gdata.gateways = ['conference-stage01.tutormeet.com:14859', 'conference-stage01.tutormeet.com:14850']
        window.GDATA = data?.gdata

        const { token, gdata = {} } = data
        const { region, wscdn: gdataWscdn } = gdata

        gdata.gateways = (gdata.gateways as string[]).map((gateway) => ({
          gateway,
          isAvailable: true,
        }))

        dispatch(setRoomGData({ gdata: data?.gdata }))
        dispatch(setRoomData({ roomData: { liveUrlInfo: data?.liveUrlInfo } }))
        dispatch(setFixedPlayerData({ fixedPlayerData: { liveUrlInfo: data?.liveUrlInfo } }))
        dispatch(setMemberRole({ role: data?.gdata.role }))

        if (socketDataRef.current.isRoomIdChanged || socketStatusRef.current !== SOCKET_STATUS.CONNECTED) {
          const gateway = find(gdata.gateways, (gateway) => gateway.isAvailable)
          if (!gateway) return
          dispatch(
            setSocketUrl({
              socketHost: gateway.gateway,
              socketToken: token,
              socketRegion: region,
              wscdn: gdataWscdn ?? '',
              roomId: sessionSn,
            }),
          )
        }
      },
      onSettled: () => {
        setIsRoomLivingDataSettled(true)
      },
    },
  )

  useEffect(() => {
    if (roomId && roomId !== sessionSn) {
      if (socketRef.current) socketRef.current.disconnect()
    }
  }, [roomId, sessionSn])

  useEffect(() => {
    // socketUrl changed -> create new socket
    if (socketUrl !== '' && socketUrlRef.current !== socketUrl) {
      console.log('shop', '[socket] connecting to:', socketUrl)
      dispatch(setSocketStatus({ socketStatus: SOCKET_STATUS.CONNECTING }))
      dispatch(setIsRoomIdChanged({ isRoomIdChanged: false }))

      // create web socket
      if (socketRef.current && socketRef.current.connected) {
        console.log('shop', '[socket] disconnect current socket')
        socketRef.current.disconnect()
      }
      socketRef.current = io(socketUrl, connectionOptions)

      socketRef.current.on('connect', () => {
        console.log('shop', '[socket] connected')
        dispatch(setSocketStatus({ socketStatus: SOCKET_STATUS.CONNECTED }))

        handleSentryEvent({
          message: '[socket]connecting',
          level: 'log',
          user: { memberInfoId },
          extra: { socketUrl },
        })
      })

      // socket.io-client v2: reason 例如 'transport close' / 'ping timeout' / 'io server disconnect'
      socketRef.current.on('disconnect', (reason: string) => {
        console.log('shop', '[socket] disconnected:', reason)
        dispatch(setReady({ isReady: false }))
        dispatch(setSocketStatus({ socketStatus: SOCKET_STATUS.DISCONNECTED }))

        reportSocketApm('DISCONNECT', reason || '')
      })

      socketRef.current.on('connect_error', (e: any) => {
        console.error('shop', '[socket] connect_error:', e)
        dispatch(setSocketStatus({ socketStatus: SOCKET_STATUS.CONNECT_ERROR }))

        reportSocketApm('CONNECT_ERROR', String(e?.message || e || ''))
      })

      socketRef.current.on('error', (e: any) => {
        console.error('shop', '[socket] error:', e)

        handleSentryEvent({
          message: '[socket] error',
          level: 'error',
          user: { memberInfoId },
          extra: { errorData: e },
        })
      })

      socketRef.current.on('reconnect', (e: any) => {
        console.log('shop', '[socket] reconnect:', e)

        handleSentryEvent({
          message: '[socket] reconnect',
          level: 'warning',
          user: { memberInfoId },
          extra: { errorData: e },
        })
      })

      socketRef.current.on('reconnecting', (e: any) => {
        console.log('shop', '[socket] reconnecting:', e)

        handleSentryEvent({
          message: '[socket] reconnecting',
          level: 'warning',
          user: { memberInfoId },
          extra: { errorData: e },
        })
      })

      socketRef.current.on('reconnect_failed', (e: any) => {
        console.error('shop', '[socket] reconnect_failed:', e)

        handleSentryEvent({
          message: '[socket] reconnect_failed',
          level: 'error',
          user: { memberInfoId },
          extra: { errorData: e },
        })
      })

      socketRef.current.on('reconnect_error', (e: any) => {
        console.error('shop', '[socket] reconnect_error`:', e)

        handleSentryEvent({
          message: '[socket] reconnect_error',
          level: 'error',
          user: { memberInfoId },
          extra: { errorData: e },
        })
      })

      const handleSocketEvent = (message?: any) => {
        const actions = handleMessageReceive({
          message,
          socketData: socketDataRef.current,
          socket: socketRef.current,
          memberData: memberDataRef.current,
          roomData: roomDataRef.current,
          t,
          brandName,
          silentUserList: socketDataRef.current?.silentUserList || [],
        })
        if (actions) {
          if (isArray(actions)) {
            actions.forEach((a: any) => {
              if (typeof a !== 'undefined') {
                try {
                  dispatch(a)
                } catch (e) {
                  console.error('shop', 'dispatch error', e)
                }
              }
            })
          } else {
            dispatch(actions)
          }
        }
      }

      socketRef.current.on('CHANNEL', handleSocketEvent) // 這裡不用 off，因為離開直播間要繼續監聽
    }

    socketUrlRef.current = socketUrl
  }, [socketUrl])

  useEffect(() => {
    const vbChangeProp: any = vbchange.getBrowserVisibilityProp()

    const onVisibilityChange = () => {
      const isHidden = vbchange.isDomHidden()
      if (!isHidden && reconnectAfterVisible) {
        console.log('shop', `[socket] after vbchang reconnect socket `)
        setReconnectAfterVisible(false)
        dispatch(setSocketStatus({ socketStatus: SOCKET_STATUS.CONNECTING }))
        socketRef.current.connect()

        reportSocketApm('RECONNECT', 'reconnect after page visible')
      }
    }

    const visibilityChangeHandle = () => {
      document.addEventListener(vbChangeProp, onVisibilityChange, false)
    }
    visibilityChangeHandle()

    const clearVisibilityChangeHandle = () => {
      document.removeEventListener(vbChangeProp, onVisibilityChange)
    }

    return () => {
      clearVisibilityChangeHandle()
    }
  }, [reconnectAfterVisible])

  useEffect(() => {
    if (socketStatusRef.current !== socketStatus) {
      // socket status changed
      // CONNECT_ERROR: try to connect to next gateway, otherwise dispatch network error
      // DISCONNECTED: try to reconnect if not isKickoff or roomIdChanged
      if (socketStatus === SOCKET_STATUS.CONNECT_ERROR) {
        const newGData = cloneDeep(gdata)
        newGData.gateways = gdata.gateways.map((item: any) => {
          if (socketData.socketHost === item.gateway) {
            return { ...item, isAvailable: false }
          }
          return { ...item }
        })
        dispatch(setRoomGData({ gdata: newGData }))

        const gateway = find(newGData.gateways, (gateway) => gateway.isAvailable)
        if (!gateway) {
          console.error('shop', '[socket] no more gateways')
          dispatch(setIsNetworkError({ isNetworkError: true }))

          reportSocketApm('NO_MORE_GATEWAYS', String(socketData.socketHost || ''))
        } else {
          dispatch(setSocketUrl({ socketHost: gateway.gateway }))
        }
      } else if (socketStatus === SOCKET_STATUS.DISCONNECTED) {
        // server will remove the disconnected user from mic list, so client needs to do it by itself as well
        const isHost = memberDataRef.current.role === ROLE.HOST || memberDataRef.current.role === ROLE.IT
        if (!isHost) {
          dispatch(setUserOffByHostOut())
          dispatch(removeMicOnItem({ uid: socketDataRef.current.userToken }))
        }

        if (!socketData.isKickoff && !socketData.isRoomIdChanged && !vbchange.isDomHidden()) {
          console.log('shop', `[socket] reconnect socket`)
          dispatch(setSocketStatus({ socketStatus: SOCKET_STATUS.CONNECTING }))
          socketRef.current.connect()

          reportSocketApm('RECONNECT', 'auto reconnect after disconnected')
        }
        if (vbchange.isDomHidden()) {
          //視窗縮小或是切換頁簽後，ws斷線，壓個flag，在視窗回來後重連
          setReconnectAfterVisible(true)
        }
      }
    }
    socketStatusRef.current = socketStatus
  }, [socketStatus, gdata, socketData])

  useEffect(() => {
    if (roomId && roomId !== sessionSn) {
      dispatch(resetRoomData())
      dispatch(resetSocket())
      dispatch(setIsRoomIdChanged({ isRoomIdChanged: true }))
    }
  }, [sessionSn, roomId])

  useEffect(() => {
    const heartbeatTimer: any = null

    if (isReady && roomId && roomId === sessionSn) {
      setUserInMutation.mutateAsync({ roomId: sessionSn })

      const isHost = memberDataRef.current.role === ROLE.HOST || memberDataRef.current.role === ROLE.IT
      if (!isHost && trackedEnterRoomRef.current !== sessionSn) {
        trackedEnterRoomRef.current = sessionSn
        trackLiveEvent({ type: LIVE_TRACK_EVENT.ROOM_ENTER, roomId: sessionSn, source: liveEnterSource(router.query) })
        setWatchingRoomId(sessionSn)
      }

      sendHeartBeat({ heartbeatTimer, socket: socketRef.current, gdata })

      getRoomSocketInfo({ socket: socketRef.current })
      getRoomSocketUsers({ socket: socketRef.current })
      getLimitMessageUsers({ socket: socketRef.current })

      sendUserCount({ socket: socketRef.current })
      sendChathistory({ socket: socketRef.current })
    }
    return () => {
      clearInterval(heartbeatTimer)
    }
  }, [isReady])

  useEffect(() => {
    if (canHandUp) {
      getRoomHandUpList({ socket: socketRef.current })
      getRoomMicOnList({ socket: socketRef.current })
    }
  }, [canHandUp])

  // PChome 合作案
  useEffect(() => {
    const handleReinitializeLiveRoomData = () => {
      dispatch(resetSocket())
      // TODO: 尚不確定是否需要 reset
      // dispatch(resetRoomData())
      dispatch(setIsRoomIdChanged({ isRoomIdChanged: true }))
      refetchRoomData()

      if (
        router.isReady &&
        !!sessionSn &&
        isLessThenOneHour &&
        isRoomInfoDataFetched &&
        (roomInfoData?.liveType === ROOM_LIVE_TYPE.CURRENT || roomInfoData?.liveType === ROOM_LIVE_TYPE.FUTURE)
      ) {
        refetchRoomLivingData()
      }
    }

    window.addEventListener('reinitializeLiveRoomData', handleReinitializeLiveRoomData)

    return () => {
      window.removeEventListener('reinitializeLiveRoomData', handleReinitializeLiveRoomData)
    }
  }, [
    isReady,
    roomId,
    !!sessionSn,
    isLessThenOneHour,
    isRoomInfoDataFetched,
    roomInfoData?.liveType === ROOM_LIVE_TYPE.CURRENT || roomInfoData?.liveType === ROOM_LIVE_TYPE.FUTURE,
  ])

  const [, setForceUpdate] = useState(false)

  // PChome 合作案
  useEffect(() => {
    if (!!sessionSn && router.isReady && !isEmpty(cookieUuid) && !isEmpty(localStorageToken)) {
      window.dispatchEvent(new Event('reinitializeLiveRoomData'))
    }
  }, [
    sessionSn,
    router.isReady,
    cookieUuid,
    localStorageToken,
    isLessThenOneHour,
    isRoomInfoDataFetched,
    roomInfoData?.liveType === ROOM_LIVE_TYPE.CURRENT || roomInfoData?.liveType === ROOM_LIVE_TYPE.FUTURE,
  ])

  // PChome 合作案
  useEffect(() => {
    const handleStorageChange = () => {
      setForceUpdate((prev) => !prev)
    }

    window.addEventListener('localStorageChanged', handleStorageChange)

    return () => {
      window.removeEventListener('localStorageChanged', handleStorageChange)
    }
  }, [])

  return {
    isRoomInfoDataFetched,
    roomLivingData: isRoomLivingDataFetched && status === 'success' && !data?.isResponseError ? data : defaultRoomLivingData,
    productList: isProductListFetched && !productList?.isResponseError ? productList : [],
    isRoomLivingDataSettled,
    setIsRoomLivingDataSettled,
    isRoomInfoDataSettled,
    setIsRoomInfoDataSettled,
  }
}
